import React from 'react';
import { SiteScaffoldSpecVersion } from '../../types';
import { versionAuthor, versionDistance } from './siteSpec';

/**
 * Every saved state of the design, and a way back to any of them.
 *
 * Every change already wrote one of these — the agent's rounds, your own field
 * edits, a brand switch, a reset — so the history was complete and completely
 * invisible. The only way back was to ask the agent to undo something, which
 * asks a model to reconstruct a state we already had stored exactly.
 *
 * SELECTING A VERSION ONLY PREVIEWS IT. Rendering a spec is a pure compile with
 * no writes, so browsing costs nothing and commits to nothing; making one
 * current is a second, deliberate press.
 *
 * Nothing is ever lost by pressing it. `record_spec_change` snapshots the state
 * AFTER each change, so the design as it stands is already the newest row in
 * this list — and the restore appends rather than truncating, so undoing an
 * undo works too.
 */
export default function SiteScaffoldVersions({
  versions,
  currentSpec,
  selected,
  busy,
  onSelect,
  onUse,
}: {
  versions: SiteScaffoldSpecVersion[];
  currentSpec: any;
  /** Which version the preview is showing, or null for the live design. */
  selected: number | null;
  busy?: boolean;
  onSelect: (version: SiteScaffoldSpecVersion | null) => void;
  onUse: (version: number) => void;
}) {
  if (!versions.length) {
    return (
      <div className="p-4 text-sm text-gray-500 dark:text-gray-400">
        No saved versions yet. Every change the designer makes, and every field you
        edit, saves one here.
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <button
        type="button"
        onClick={() => onSelect(null)}
        className={`w-full text-left px-3 py-2 border-b border-gray-200 dark:border-gray-700 ${
          selected === null ? 'bg-blue-50 dark:bg-blue-900/20' : ''
        }`}
      >
        <div className="text-sm text-gray-900 dark:text-gray-100">
          The design as it stands
        </div>
        <div className="text-[11px] text-gray-500 dark:text-gray-400">
          What the preview and the fields show. It is also the newest row below.
        </div>
      </button>

      {versions.map((version) => {
        const active = selected === version.version;
        return (
          <div
            key={version.id}
            className={`border-b border-gray-200 dark:border-gray-700 ${
              active ? 'bg-blue-50 dark:bg-blue-900/20' : ''
            }`}
          >
            <button
              type="button"
              onClick={() => onSelect(active ? null : version)}
              className="w-full text-left px-3 py-2"
            >
              <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm text-gray-900 dark:text-gray-100">
                  {version.note || `Version ${version.version}`}
                </span>
                <span className="text-[11px] text-gray-400 dark:text-gray-500 whitespace-nowrap">
                  {new Date(version.created_at).toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
              </div>
              <div className="text-[11px] text-gray-500 dark:text-gray-400">
                {versionAuthor(version.authored_by)} ·{' '}
                {versionDistance(version.spec, currentSpec)}
              </div>
            </button>

            {active && (
              <div className="px-3 pb-2 flex items-center gap-2">
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => onUse(version.version)}
                  className="px-2.5 py-1 text-xs rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  {busy ? 'Applying…' : 'Use this version'}
                </button>
                <span className="text-[11px] text-gray-500 dark:text-gray-400">
                  The design as it stands stays in this list.
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
